import React from 'react';
import SEO from '../components/SEO';
import TestimonialsSection from '../components/TestimonialsSection';
import FAQSection from '../components/FAQSection';
import { Award, Target, Shield, Users, CheckCircle, TrendingUp } from 'lucide-react';

const AboutPage = () => {
  return (
    <>
      <SEO 
        title="About Us | Norfolk Trademark - Expert Trademark Registration"
        description="Learn about Norfolk Trademark's mission to provide affordable, professional trademark registration services with licensed USPTO attorneys."
        keywords="about trademark registration, USPTO attorneys, trademark services, intellectual property experts"
        canonical="/about"
      />
      
      {/* Hero Section */}
      <div className="bg-gradient-to-br from-[#FF035B] via-[#2F1A8E] to-black text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6">About Norfolk Trademark</h1>
            <p className="text-xl text-white/90 max-w-3xl mx-auto">
              Your trusted partner in trademark registration and brand protection since 2010
            </p>
          </div>
        </div>
      </div>

      {/* About the Company */}
      <div className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-block bg-[#FF035B]/10 text-[#FF035B] px-4 py-2 rounded-full text-sm font-semibold mb-6">
                About the Company
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-[#404138] mb-6">
                Protecting Brands, Empowering Businesses
              </h2>
              <p className="text-lg text-[#6E6B6A] mb-6">
                Founded in 2010, Norfolk Trademark has grown from a small legal practice to one of the nation&apos;s leading trademark registration services. We&apos;ve helped over 10,000 businesses protect their brands, from ambitious startups to established enterprises.
              </p>
              <p className="text-lg text-[#6E6B6A] mb-6">
                Our journey began with a simple observation: trademark registration was too complex, too expensive, and too intimidating for most small business owners. We set out to change that by combining legal expertise with modern technology and transparent pricing.
              </p>
              <p className="text-lg text-[#6E6B6A]">
                Today, we&apos;re proud to be the go-to trademark service for entrepreneurs who want professional protection without the traditional law firm price tag. Every member of our team is committed to making brand protection accessible, affordable, and straightforward.
              </p>
            </div>
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1600880292203-757bb62b4baf?w=600&q=80"
                alt="Our team at work"
                className="rounded-2xl shadow-2xl w-full h-auto object-cover"
              />
              <div className="absolute -bottom-6 -left-6 bg-white rounded-xl shadow-lg p-5 hidden lg:block border border-gray-100">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-[#FF035B]/10 rounded-full flex items-center justify-center">
                    <Shield className="w-5 h-5 text-[#FF035B]" />
                  </div>
                  <div>
                    <p className="text-sm font-bold text-gray-900">Trusted Partner</p>
                    <p className="text-xs text-gray-500">Since 2010</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Vision and Mission */}
      <div className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-block bg-[#FF035B]/10 text-[#FF035B] px-4 py-2 rounded-full text-sm font-semibold mb-6">
              Our Vision & Mission
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-[#404138] mb-6">
              Building a World Where Every Brand is Protected
            </h2>
          </div>

          {/* Vision - Left Image, Right Content */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10 items-center">
            <div className="rounded-2xl overflow-hidden shadow-lg">
              <img 
                src="https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=700&q=70" 
                alt="Our Vision" 
                className="w-full h-[350px] object-cover"
              />
            </div>
            <div>
              <div className="w-14 h-14 bg-[#FF035B]/10 rounded-xl flex items-center justify-center mb-5">
                <Target className="w-7 h-7 text-[#FF035B]" />
              </div>
              <h3 className="text-3xl font-bold text-[#404138] mb-4">Our Vision</h3>
              <p className="text-[#6E6B6A] leading-relaxed text-base">
                To become the most trusted and accessible trademark protection service in the United States, where every entrepreneur—regardless of budget or experience—can confidently protect their brand and build lasting business value. We envision a future where trademark registration is as simple as registering a domain name, yet backed by expert legal guidance.
              </p>
            </div>
          </div>

          {/* Mission - Left Content, Right Image */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10 items-center mt-16">
            <div className="order-2 md:order-1">
              <div className="w-14 h-14 bg-[#2F1A8E]/10 rounded-xl flex items-center justify-center mb-5">
                <Shield className="w-7 h-7 text-[#2F1A8E]" />
              </div>
              <h3 className="text-3xl font-bold text-[#404138] mb-4">Our Mission</h3>
              <p className="text-[#6E6B6A] leading-relaxed text-base">
                To empower businesses with professional trademark protection through transparent pricing, expert legal guidance, and cutting-edge technology. We exist to eliminate the confusion and complexity from brand protection, delivering fast, affordable, and reliable trademark registration services that actually get approved. Every trademark we file is handled with the same care and expertise as if it were our own.
              </p>
            </div>
            <div className="order-1 md:order-2 rounded-2xl overflow-hidden shadow-lg">
              <img 
                src="https://images.unsplash.com/photo-1521791136064-7986c2920216?w=700&q=70" 
                alt="Our Mission" 
                className="w-full h-[350px] object-cover"
              />
            </div>
          </div>

          {/* Core Values */}
          <div className="mt-16">
            <h3 className="text-3xl font-bold text-[#404138] text-center mb-12">Our Core Values</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="w-16 h-16 bg-[#FF035B]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Award className="w-8 h-8 text-[#FF035B]" />
                </div>
                <h4 className="text-xl font-bold text-[#404138] mb-3">Excellence</h4>
                <p className="text-[#6E6B6A]">
                  We maintain the highest standards in every trademark application, combining legal expertise with meticulous attention to detail.
                </p>
              </div>

              <div className="text-center">
                <div className="w-16 h-16 bg-[#2F1A8E]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Users className="w-8 h-8 text-[#2F1A8E]" />
                </div>
                <h4 className="text-xl font-bold text-[#404138] mb-3">Transparency</h4>
                <p className="text-[#6E6B6A]">
                  No hidden fees, no surprises. We believe in upfront pricing and clear communication throughout the entire process.
                </p>
              </div>

              <div className="text-center">
                <div className="w-16 h-16 bg-[#FF035B]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <TrendingUp className="w-8 h-8 text-[#FF035B]" />
                </div>
                <h4 className="text-xl font-bold text-[#404138] mb-3">Innovation</h4>
                <p className="text-[#6E6B6A]">
                  We leverage AI and modern technology to deliver faster, more accurate trademark searches and filings than traditional firms.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Why Choose Us */}
      <div className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-block bg-[#FF035B]/10 text-[#FF035B] px-4 py-2 rounded-full text-sm font-semibold mb-6">
              Why Choose Us
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-[#404138] mb-6">
              What Sets Us Apart From Traditional Law Firms
            </h2>
            <p className="text-xl text-[#6E6B6A] max-w-3xl mx-auto">
              We combine the expertise of licensed USPTO attorneys with modern technology and transparent pricing to deliver unmatched value.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-gradient-to-br from-gray-50 to-white border border-gray-200 rounded-2xl p-8 hover:shadow-xl transition-all duration-300 hover:border-[#FF035B]">
              <div className="w-12 h-12 bg-[#FF035B] text-white rounded-xl flex items-center justify-center mb-6 font-bold text-xl">
                1
              </div>
              <h3 className="text-xl font-bold text-[#404138] mb-4">98% Approval Rate</h3>
              <p className="text-[#6E6B6A]">
                Our experienced attorneys know exactly what works with the USPTO. We&apos;ve mastered the application process, resulting in one of the industry&apos;s highest approval rates.
              </p>
            </div>

            <div className="bg-gradient-to-br from-gray-50 to-white border border-gray-200 rounded-2xl p-8 hover:shadow-xl transition-all duration-300 hover:border-[#2F1A8E]">
              <div className="w-12 h-12 bg-[#2F1A8E] text-white rounded-xl flex items-center justify-center mb-6 font-bold text-xl">
                2
              </div>
              <h3 className="text-xl font-bold text-[#404138] mb-4">24-48 Hour Filing</h3>
              <p className="text-[#6E6B6A]">
                Speed matters in trademark protection. We file your application within 24-48 hours, securing your priority date before competitors can claim your mark.
              </p>
            </div>

            <div className="bg-gradient-to-br from-gray-50 to-white border border-gray-200 rounded-2xl p-8 hover:shadow-xl transition-all duration-300 hover:border-[#FF035B]">
              <div className="w-12 h-12 bg-[#FF035B] text-white rounded-xl flex items-center justify-center mb-6 font-bold text-xl">
                3
              </div>
              <h3 className="text-xl font-bold text-[#404138] mb-4">Zero Hidden Fees</h3>
              <p className="text-[#6E6B6A]">
                What you see is what you pay. Our pricing is transparent and all-inclusive—no surprise bills, no additional charges for basic services.
              </p>
            </div>

            <div className="bg-gradient-to-br from-gray-50 to-white border border-gray-200 rounded-2xl p-8 hover:shadow-xl transition-all duration-300 hover:border-[#2F1A8E]">
              <div className="w-12 h-12 bg-[#2F1A8E] text-white rounded-xl flex items-center justify-center mb-6 font-bold text-xl">
                4
              </div>
              <h3 className="text-xl font-bold text-[#404138] mb-4">Licensed USPTO Attorneys</h3>
              <p className="text-[#6E6B6A]">
                Every application is prepared and reviewed by licensed USPTO attorneys with years of trademark experience—not paralegals or document processors.
              </p>
            </div>

            <div className="bg-gradient-to-br from-gray-50 to-white border border-gray-200 rounded-2xl p-8 hover:shadow-xl transition-all duration-300 hover:border-[#FF035B]">
              <div className="w-12 h-12 bg-[#FF035B] text-white rounded-xl flex items-center justify-center mb-6 font-bold text-xl">
                5
              </div>
              <h3 className="text-xl font-bold text-[#404138] mb-4">AI-Powered Searches</h3>
              <p className="text-[#6E6B6A]">
                Our proprietary AI technology scans millions of trademarks in seconds, identifying potential conflicts that manual searches might miss.
              </p>
            </div>

            <div className="bg-gradient-to-br from-gray-50 to-white border border-gray-200 rounded-2xl p-8 hover:shadow-xl transition-all duration-300 hover:border-[#2F1A8E]">
              <div className="w-12 h-12 bg-[#2F1A8E] text-white rounded-xl flex items-center justify-center mb-6 font-bold text-xl">
                6
              </div>
              <h3 className="text-xl font-bold text-[#404138] mb-4">Money-Back Guarantee</h3>
              <p className="text-[#6E6B6A]">
                If our comprehensive search reveals your mark isn&apos;t available for registration, we&apos;ll refund your search fee. Your satisfaction is guaranteed.
              </p>
            </div>
          </div>

          {/* Additional Benefits */}
          <div className="mt-16 bg-gradient-to-r from-[#FF035B] to-[#2F1A8E] rounded-2xl p-8 md:p-12 text-white">
            <h3 className="text-3xl font-bold mb-8 text-center">Plus, You Get These Benefits</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="flex items-start gap-4">
                <CheckCircle className="w-6 h-6 flex-shrink-0 mt-1" />
                <div>
                  <h4 className="font-bold text-lg mb-2">Dedicated Support Team</h4>
                  <p className="text-white/90">Direct access to your attorney via email and phone throughout the entire process.</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <CheckCircle className="w-6 h-6 flex-shrink-0 mt-1" />
                <div>
                  <h4 className="font-bold text-lg mb-2">Office Action Response</h4>
                  <p className="text-white/90">If USPTO issues an office action, we handle the response at no additional charge.</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <CheckCircle className="w-6 h-6 flex-shrink-0 mt-1" />
                <div>
                  <h4 className="font-bold text-lg mb-2">Lifetime Monitoring</h4>
                  <p className="text-white/90">We track your trademark status and alert you to important deadlines and potential conflicts.</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <CheckCircle className="w-6 h-6 flex-shrink-0 mt-1" />
                <div>
                  <h4 className="font-bold text-lg mb-2">Renewal Reminders</h4>
                  <p className="text-white/90">Never miss a critical maintenance deadline with our automated reminder system.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Testimonials Section */}
      <TestimonialsSection />

      {/* FAQ Section */}
      <FAQSection />

      {/* CTA Section */}
      <div className="bg-gradient-to-br from-gray-900 via-black to-gray-900 text-white py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Ready to Protect Your Brand?
          </h2>
          <p className="text-xl text-gray-300 mb-8">
            Join thousands of businesses who trust us with their trademark protection
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="/contact"
              className="bg-[#FF035B] hover:bg-[#2F1A8E] text-white font-bold px-8 py-3 rounded-lg transition-all duration-300 transform hover:scale-105"
            >
              Get Started Today
            </a>
            <a
              href="/pricing"
              className="bg-white text-[#FF035B] hover:bg-gray-100 font-bold px-8 py-4 rounded-lg transition-all duration-300"
            >
              View Pricing
            </a>
          </div>
        </div>
      </div>
    </>
  );
};

export default AboutPage;
